import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { intervalSocketBunja } from '@flipster/r230522/original/interval/socket';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import { IntervalData, IntervalDataUpdate, WsV2TableEvent } from '@flipster/r230522/original/types';
import { getTradingViewDataStorageAtom } from '@flipster/tvchart/src/misc/data-manager';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { SymbolScope } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { pipChartPriceType } from './constants';

export const PointCount = 20;

export const pipChartDataAtom = atom<string[]>([]);
pipChartDataAtom.debugLabel = 'pipChartDataAtom';

export const pipChartDataBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const symbol = bunja.use(SymbolScope);
  const { requestIntervalSnapshot, r230522Socket } = bunja.use(intervalSocketBunja);
  const { pswapTickersAtom } = bunja.use(pswapTickersBunja);
  const intervalAtom = getTradingViewDataStorageAtom('interval');

  const pipChartDataQueryAtom = atomWithQuery((get) => {
    const interval = get(intervalAtom);

    const requestIntervalDataKey = `${symbol}:${pipChartPriceType}:${interval}`;

    return {
      queryKey: [requestIntervalDataKey],
      queryFn: () => requestIntervalSnapshot(requestIntervalDataKey, PointCount),
      select: (res: IntervalData[]) => res.map((data) => data.c),
      refetchOnMount: 'always',
    };
  });

  const handler = (message: WsV2TableEvent<IntervalData | IntervalDataUpdate>) => {
    const interval = store.get(intervalAtom);
    const requestIntervalDataKey = `${symbol}:${pipChartPriceType}:${interval}`;

    const { i, u } = message.data;
    if (i) {
      const lastPrice = i[requestIntervalDataKey]?.c;
      if (lastPrice) {
        store.set(pipChartDataAtom, (prev) => {
          if (prev.length < PointCount) return [...prev, lastPrice];
          return [...prev.slice(1), lastPrice];
        });
      }
    }
    if (u) {
      const lastPrice = u[requestIntervalDataKey]?.c;
      if (lastPrice) {
        store.set(pipChartDataAtom, (prev) => [...prev.slice(0, prev.length - 1), lastPrice]);
      }
    }
  };

  const getTickerAtom = atom((get) => {
    const tickers = get(pswapTickersAtom);
    if (!symbol || !tickers) return undefined;
    return tickers[symbol];
  });

  bunja.effect(() => {
    const unsub = store.sub(pipChartDataQueryAtom, () => {
      store.set(pipChartDataAtom, store.get(pipChartDataQueryAtom).data || []);
    });

    r230522Socket.on('market/interval-data', handler);
    return () => {
      r230522Socket.off('market/interval-data', handler);
      unsub();
    };
  });

  return {
    pipChartDataAtom,
    getTickerAtom,
  };
});
pipChartDataBunja.debugLabel = 'pipChartDataBunja';
